/**
 * src/components/providers/ActiveSpaceProvider.tsx
 * =============================================================================
 * Context global con el "espacio activo" del user — el del switcher del
 * UserMenu. El resto de la app lo lee con `useActiveSpace()` para:
 *  - Pasarlo como `spaceId` en /me/documentos y /me/quota.
 *  - Decidir si mostrar UI específica del espacio (botón "Miembros" en el
 *    drawer solo aparece si el espacio activo NO es personal).
 *
 * Persistencia: `localStorage.activeSpaceId`. Sobrevive a recargas. Si el
 * id guardado ya no aparece en `listSpaces()` (el user salió o el espacio
 * fue borrado), fallback al personal.
 *
 * Refresh: el provider llama `listSpaces()` al montar (si hay sesión) y
 * tras login/logout. La función `refresh()` se expone por si un caller
 * acaba de crear un espacio nuevo y quiere refrescar.
 * =============================================================================
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { listSpaces, type SpaceListItem } from '@/services/spacesService'
import { useAuth } from '@/components/providers/AuthProvider'

const STORAGE_KEY = 'ploter:activeSpaceId'

interface ActiveSpaceContextValue {
  spaces: SpaceListItem[]
  activeSpaceId: string | null
  /** El SpaceListItem activo (resuelto a partir de id). Null si la lista
   *  aún no cargó. Útil para mostrar el nombre/rol en la UI. */
  active: SpaceListItem | null
  /** Cambia el espacio activo. Persiste en localStorage. */
  setActiveSpaceId: (id: string) => void
  /** Re-fetch de la lista (post create / leave / accept invite). */
  refresh: () => Promise<void>
  loading: boolean
}

const ActiveSpaceContext = createContext<ActiveSpaceContextValue | undefined>(undefined)

export function ActiveSpaceProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const [spaces, setSpaces] = useState<SpaceListItem[]>([])
  const [loading, setLoading] = useState(false)
  // Inicializamos con lo que haya en localStorage. Si tras cargar la lista
  // ese id ya no existe en `spaces`, caemos al personal.
  const [activeSpaceId, setActiveSpaceIdState] = useState<string | null>(() => {
    try {
      return window.localStorage.getItem(STORAGE_KEY)
    } catch {
      return null
    }
  })

  /** Token de carrera de la última invocación de `refresh()`. Cada call
   *  incrementa el ref; sólo el call CON el mayor token aplicará su
   *  resultado. Sin esto, un fetch en vuelo al hacer logout podía
   *  llegar después del reset y "resucitar" la lista del user previo. */
  const refreshTokenRef = useRef(0)

  const refresh = useCallback(async () => {
    const myToken = ++refreshTokenRef.current
    if (!user) {
      setSpaces([])
      return
    }
    setLoading(true)
    try {
      const list = await listSpaces()
      if (refreshTokenRef.current !== myToken) return // race: hay un refresh posterior
      setSpaces(list)
    } catch {
      // No bloqueamos la app si falla (red caída, 401 que ya invalidó la
      // sesión, etc.). Mantenemos lo último que tuviéramos.
    } finally {
      if (refreshTokenRef.current === myToken) setLoading(false)
    }
  }, [user])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void refresh()
  }, [refresh])

  // Sincronizar `activeSpaceId` con la lista actual: si el id guardado no
  // está en `spaces`, fallback al personal. El setState es intencional
  // para mantener `activeSpaceId` consistente con la lista que viene del
  // servidor — caso canónico de "sync external state into React".
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (spaces.length === 0) return
    const exists = activeSpaceId && spaces.some((s) => s.id === activeSpaceId)
    if (!exists) {
      const personal = spaces.find((s) => s.isPersonal) ?? spaces[0]
      if (personal) {
        setActiveSpaceIdState(personal.id)
        try {
          window.localStorage.setItem(STORAGE_KEY, personal.id)
        } catch {
          /* localStorage puede fallar en modo privado — sin persistencia */
        }
      }
    }
  }, [spaces, activeSpaceId])
  /* eslint-enable react-hooks/set-state-in-effect */

  const setActiveSpaceId = useCallback((id: string) => {
    setActiveSpaceIdState(id)
    try {
      window.localStorage.setItem(STORAGE_KEY, id)
    } catch {
      /* sin persistencia */
    }
  }, [])

  const active = useMemo(
    () => spaces.find((s) => s.id === activeSpaceId) ?? null,
    [spaces, activeSpaceId],
  )

  return (
    <ActiveSpaceContext.Provider
      value={{ spaces, activeSpaceId, active, setActiveSpaceId, refresh, loading }}
    >
      {children}
    </ActiveSpaceContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useActiveSpace(): ActiveSpaceContextValue {
  const ctx = useContext(ActiveSpaceContext)
  if (!ctx) {
    throw new Error('useActiveSpace debe usarse dentro de <ActiveSpaceProvider>')
  }
  return ctx
}
